import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, X, Code, MessageCircle, Zap, Upload, Loader2, Image, FileVideo, File, Mic } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { toUserFriendlyError } from '@/lib/error-messages';
import { nomeDoArquivo } from '@shared/flow-node-summary';

interface MessageButton {
  id: string;
  text: string;
  type: 'url' | 'phone' | 'text' | 'postback';
  value: string;
}

interface MessageContent {
  type: 'text' | 'image' | 'video' | 'audio' | 'file';
  text?: string;
  url?: string;
  /** Nome original do arquivo enviado — é o que o WhatsApp mostra no anexo. */
  fileName?: string;
  caption?: string;
  buttons?: MessageButton[];
}

interface MessageEditorProps {
  content: MessageContent;
  onChange: (content: MessageContent) => void;
}

const VARIABLES = [
  { name: '{{nome}}', description: 'Nome do contato' },
  { name: '{{email}}', description: 'Email do contato' },
  { name: '{{telefone}}', description: 'Telefone do contato' },
  { name: '{{tag}}', description: 'Tag do contato' },
  { name: '{{data}}', description: 'Data atual' },
  { name: '{{hora}}', description: 'Hora atual' },
];

const CONTENT_TYPE_CONFIG = [
  { value: 'text', label: 'Texto', icon: MessageCircle },
  { value: 'image', label: 'Imagem', icon: Image },
  { value: 'video', label: 'Vídeo', icon: FileVideo },
  { value: 'audio', label: 'Áudio', icon: Mic },
  { value: 'file', label: 'Arquivo', icon: File },
] as const;

const ACCEPTED_TYPES: Record<string, string> = {
  image: 'image/jpeg,image/png,image/gif,image/webp',
  video: 'video/mp4,video/webm,video/quicktime',
  audio: 'audio/mpeg,audio/mp3,audio/ogg,audio/wav,audio/aac,audio/m4a',
  file: '*/*',
};

const MEDIA_LABELS: Record<string, string> = {
  image: 'Imagem',
  video: 'Vídeo',
  audio: 'Áudio',
  file: 'Arquivo',
};

const MEDIA_HINTS: Record<string, string> = {
  image: 'JPG, PNG, GIF, WebP (máx. 16MB)',
  video: 'MP4, WebM, MOV (máx. 16MB)',
  audio: 'MP3, OGG, WAV, AAC, M4A (máx. 16MB)',
  file: 'Qualquer tipo de arquivo (máx. 16MB)',
};

/**
 * Como o contato vê a mensagem.
 *
 * Antes isto era uma terceira aba, ao lado de "Editor" e "Botões" — ou seja,
 * invisível justamente enquanto a pessoa escreve. Agora aparece embaixo do
 * campo, mudando junto.
 */
function PreviaDoWhatsApp({ content }: { content: MessageContent }) {
  const temMidia = content.type !== 'text' && !!content.url?.trim();
  const ICONES: Record<string, any> = { image: Image, video: FileVideo, audio: Mic, file: File };
  const IconeMidia = ICONES[content.type] ?? File;
  const legenda = content.caption?.trim() || content.text?.trim() || '';
  const roteaveis = (content.buttons ?? []).filter((b) => b.type === 'text' || b.type === 'postback');
  const links = (content.buttons ?? []).filter((b) => b.type === 'url' || b.type === 'phone');

  return (
    <div className="rounded-lg bg-[#e6ddd4] p-3 space-y-1.5">
      <div className="max-w-[260px] rounded-lg rounded-tl-sm bg-white shadow-sm overflow-hidden">
        {temMidia && content.type === 'image' && (
          <img
            src={content.url}
            alt=""
            className="w-full max-h-36 object-cover"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        )}
        {temMidia && content.type !== 'image' && (
          <div className="flex items-center gap-2 px-2.5 pt-2.5">
            <IconeMidia className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="text-xs text-foreground/80 truncate">
              {content.fileName || nomeDoArquivo(content.url)}
            </span>
          </div>
        )}
        <div className="px-2.5 py-2">
          {legenda ? (
            <p className="text-[13px] leading-snug whitespace-pre-wrap break-words text-foreground">{legenda}</p>
          ) : (
            <p className="text-[13px] italic text-muted-foreground">
              {temMidia ? 'Sem legenda' : 'A mensagem ainda está vazia'}
            </p>
          )}
          {content.type === 'audio' && content.text?.trim() && (
            <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
              O WhatsApp não aceita legenda em áudio — este texto vai numa mensagem separada.
            </p>
          )}
        </div>
        {roteaveis.length > 0 && (
          <div className="border-t border-black/5">
            {roteaveis.map((b) => (
              <div key={b.id} className="border-b border-black/5 last:border-b-0 px-2 py-1.5 text-center text-[12px] text-[#00a5f4]">
                {b.text}
              </div>
            ))}
          </div>
        )}
      </div>
      {links.length > 0 && (
        <p className="text-[10px] text-black/50 max-w-[260px] leading-snug">
          {links.length === 1
            ? 'O link vira um botão clicável na bolha.'
            : 'Com mais de um link, todos vão listados no fim do texto.'}
        </p>
      )}
    </div>
  );
}

export const MessageEditor: React.FC<MessageEditorProps> = ({ content, onChange }) => {
  const [newButton, setNewButton] = useState<Partial<MessageButton>>({ type: 'url' });
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // `document.getElementById('message-text')` era um id global: com dois
  // editores montados (nó aberto + preview), inserir variável escrevia no
  // textarea errado.
  const textoRef = useRef<HTMLTextAreaElement>(null);
  const nomeDoUploadRef = useRef<string>('');

  const uploadMutation = trpc.upload.media.useMutation({
    onSuccess: (result) => {
      onChange({
        ...content,
        // O tipo escolhido manda. Trocar para `detectedType` fazia quem
        // escolheu "Arquivo" e subiu um PNG receber uma imagem — e perder o
        // envio como documento, que é o que preserva o arquivo original.
        type: content.type === 'text' ? (result.detectedType as MessageContent['type']) : content.type,
        url: result.url,
        fileName: nomeDoUploadRef.current || undefined,
      });
      toast.success('Arquivo enviado.');
    },
    onError: (err) => {
      toast.error(toUserFriendlyError(err, 'Erro ao enviar arquivo'));
    },
    onSettled: () => setIsUploading(false),
  });

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 16 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 16MB');
      return;
    }

    setIsUploading(true);
    nomeDoUploadRef.current = file.name;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      uploadMutation.mutate({
        base64,
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
      });
    };
    reader.onerror = () => {
      toast.error('Erro ao ler o arquivo.');
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAddButton = () => {
    const type = (newButton.type || 'url') as MessageButton['type'];
    const buttonText = (newButton.text || '').trim();
    const buttonValue = (newButton.value || '').trim();

    if (!buttonText) {
      toast.error('Texto do botão é obrigatório.');
      return;
    }
    // 'text' (Resposta de Texto): o próprio texto do botão É a resposta —
    //   não exigimos campo extra.
    // Demais tipos exigem 'value' (URL, telefone, ou payload da ação).
    if (type !== 'text' && !buttonValue) {
      toast.error('Preencha o valor do botão.');
      return;
    }

    // Valida URL absoluta — sem http(s):// o WhatsApp rejeita o CTA e o
    // simulador (window.open) trata o valor como path relativo, abrindo
    // no domínio do construtor (ex: /whatsapp-flows/<texto-do-body>) em
    // vez do destino real.
    if (type === 'url' && !/^https?:\/\//i.test(buttonValue)) {
      toast.error('URL deve começar com http:// ou https://');
      return;
    }

    const button: MessageButton = {
      id: Math.random().toString(36).substr(2, 9),
      text: buttonText,
      type,
      // text → value=text (redundante mas mantém o shape consistente);
      // postback → value é o payload customizado (ex: "human_handoff");
      // url/phone → value é a URL/número.
      value: type === 'text' ? buttonText : buttonValue,
    };

    onChange({
      ...content,
      buttons: [...(content.buttons || []), button],
    });
    setNewButton({ type: 'url' });
  };

  const handleRemoveButton = (id: string) => {
    onChange({
      ...content,
      buttons: content.buttons?.filter(b => b.id !== id) || [],
    });
  };

  const handleInsertVariable = (variable: string) => {
    const textarea = textoRef.current;
    const text = content.text || '';
    // Sem foco no campo, `selectionStart` é 0 — não `undefined`. O `??` não
    // pegava, e clicar numa variável sem antes clicar no texto a inseria no
    // COMEÇO da mensagem. `document.activeElement` é quem sabe se o cursor
    // está mesmo ali.
    const comFoco = !!textarea && document.activeElement === textarea;
    const start = comFoco ? textarea.selectionStart : text.length;
    const end = comFoco ? textarea.selectionEnd : text.length;
    const novo = text.substring(0, start) + variable + text.substring(end);
    onChange({ ...content, text: novo });
    requestAnimationFrame(() => {
      textarea?.focus();
      const cursor = start + variable.length;
      textarea?.setSelectionRange(cursor, cursor);
    });
  };

  return (
    // Com o painel largo, a prévia sobe para o lado do campo em vez de ficar
    // embaixo: quem escreve consegue ver o efeito sem rolar. O `@` mede a
    // largura do PAINEL, não da janela — ver PainelLateral.
    <div className="space-y-3 @2xl:grid @2xl:grid-cols-[minmax(0,1fr)_260px] @2xl:gap-4 @2xl:space-y-0 @2xl:items-start">
      <Tabs defaultValue="editor" className="w-full @2xl:min-w-0">
        <TabsList className="grid w-full grid-cols-2 h-8">
          <TabsTrigger value="editor" className="text-xs gap-1.5">
            <MessageCircle className="w-3.5 h-3.5" />
            Editor
          </TabsTrigger>
          <TabsTrigger value="buttons" className="text-xs gap-1.5">
            <Zap className="w-3.5 h-3.5" />
            Botões
          </TabsTrigger>
        </TabsList>

        <TabsContent value="editor" className="space-y-3 mt-3">
          {/* Content Type Selector */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Tipo de Conteúdo</label>
            <div className="grid grid-cols-5 gap-1.5">
              {CONTENT_TYPE_CONFIG.map(({ value, label, icon: Icon }) => (
                <Button
                  key={value}
                  variant={content.type === value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onChange({ ...content, type: value as MessageContent['type'] })}
                  className="flex flex-col items-center gap-0.5 h-auto py-1.5 px-1"
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="text-[10px]">{label}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Text Editor */}
          {content.type === 'text' && (
            <div className="space-y-3">
              <div>
                <label htmlFor="message-text" className="text-sm font-medium">
                  Mensagem de Texto
                </label>
                <Textarea
                  ref={textoRef}
                  id="message-text"
                  placeholder="Escreva como se estivesse no WhatsApp…"
                  value={content.text || ''}
                  onChange={e => onChange({ ...content, text: e.target.value })}
                  className="min-h-28 mt-1.5 text-sm"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Use variáveis dinâmicas para personalizar
                </p>
              </div>

              <Card className="bg-muted/50">
                <CardHeader className="pb-1.5 pt-2.5 px-3">
                  <CardTitle className="text-xs">Variáveis Dinâmicas</CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-2.5">
                  <div className="grid grid-cols-2 gap-1">
                    {VARIABLES.map(variable => (
                      <Button
                        key={variable.name}
                        variant="outline"
                        size="sm"
                        onClick={() => handleInsertVariable(variable.name)}
                        className="justify-start text-[11px] h-6 px-2"
                        title={variable.description}
                      >
                        <Code className="w-3 h-3 mr-1 shrink-0" />
                        {variable.name}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Media Upload (image, video, audio, file) */}
          {content.type !== 'text' && (
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES[content.type] || '*/*'}
                onChange={handleFileUpload}
                className="hidden"
              />

              <div>
                <label className="text-sm font-medium mb-1.5 block">
                  {MEDIA_LABELS[content.type] || 'Arquivo'}
                </label>

                {content.url ? (
                  <div className="space-y-2">
                    {/* Preview */}
                    {content.type === 'image' && (
                      <div className="rounded-lg overflow-hidden border bg-muted/30">
                        <img
                          src={content.url}
                          alt="preview"
                          className="max-w-full h-auto max-h-40 object-contain mx-auto"
                        />
                      </div>
                    )}
                    {content.type === 'video' && (
                      <div className="rounded-lg overflow-hidden border bg-muted/30">
                        <video src={content.url} controls className="max-w-full h-auto max-h-40 mx-auto" />
                      </div>
                    )}
                    {content.type === 'audio' && (
                      <div className="rounded-lg border bg-muted/30 p-3">
                        <audio src={content.url} controls className="w-full" />
                      </div>
                    )}
                    {content.type === 'file' && (
                      <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30">
                        <File className="w-5 h-5 text-muted-foreground shrink-0" />
                        {/* A url é /api/media/37 — não é nome nem link que a
                            pessoa abra. Mostrar isso como se fosse informação
                            útil só confundia. */}
                        <span className="text-sm truncate flex-1">
                          {content.fileName || nomeDoArquivo(content.url)}
                        </span>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                      >
                        <Upload className="w-3.5 h-3.5 mr-1.5" />
                        Trocar
                      </Button>
                      <Button variant="ghost" size="sm" title="Remover arquivo" onClick={() => onChange({ ...content, url: '', fileName: undefined })}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="border-2 border-dashed rounded-lg p-5 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
                    onClick={() => !isUploading && fileInputRef.current?.click()}
                  >
                    {isUploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-7 h-7 text-primary animate-spin" />
                        <p className="text-xs text-muted-foreground">Enviando...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1.5">
                        <Upload className="w-7 h-7 text-muted-foreground" />
                        <p className="text-sm font-medium">Clique para upload</p>
                        <p className="text-[11px] text-muted-foreground">
                          {MEDIA_HINTS[content.type]}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* URL manual — some depois de um upload: ali a url é o caminho
                  interno (/api/media/25), que não é nem nome nem link útil. */}
              <div className={content.fileName ? 'hidden' : undefined}>
                <label className="text-[11px] text-muted-foreground mb-1 block">
                  Ou cole uma URL https pública
                </label>
                <Input
                  placeholder={`https://exemplo.com/${content.type === 'image' ? 'imagem.jpg' : content.type === 'video' ? 'video.mp4' : content.type === 'audio' ? 'audio.mp3' : 'arquivo.pdf'}`}
                  value={content.url || ''}
                  onChange={e => onChange({ ...content, url: e.target.value, fileName: undefined })}
                  className="text-sm"
                />
              </div>

              {/* Caption for images */}
              {content.type === 'image' && (
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Legenda (opcional)</label>
                  <Input
                    placeholder="Legenda da imagem"
                    value={content.caption || ''}
                    onChange={e => onChange({ ...content, caption: e.target.value })}
                    className="text-sm"
                  />
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="buttons" className="space-y-3 mt-3">
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Adicionar Botão</label>
              <div className="space-y-2 mt-2">
                <Input
                  placeholder="Texto do botão"
                  value={newButton.text || ''}
                  onChange={e => setNewButton({ ...newButton, text: e.target.value })}
                  className="text-sm"
                />
                <select
                  className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background"
                  value={newButton.type || 'url'}
                  onChange={e => setNewButton({ ...newButton, type: e.target.value as MessageButton['type'], value: '' })}
                >
                  <option value="url">Link (URL)</option>
                  <option value="phone">Telefone</option>
                  <option value="text">Resposta de Texto</option>
                  <option value="postback">Ação Customizada</option>
                </select>
                {newButton.type === 'text' ? (
                  <p className="text-xs text-muted-foreground px-1">
                    O próprio texto do botão é a resposta enviada quando o cliente clica.
                  </p>
                ) : (
                  <>
                    <Input
                      placeholder={
                        newButton.type === 'phone'
                          ? '+55 11 99999-9999'
                          : newButton.type === 'url'
                          ? 'https://exemplo.com'
                          : 'Ex: human_handoff'
                      }
                      value={newButton.value || ''}
                      onChange={e => setNewButton({ ...newButton, value: e.target.value })}
                      className="text-sm"
                    />
                    {newButton.type === 'postback' && (
                      <p className="text-xs text-muted-foreground px-1">
                        Identificador interno enviado pelo botão (não aparece pro cliente). Útil pra integração externa, analytics e roteamento por payload.
                      </p>
                    )}
                  </>
                )}
                <Button onClick={handleAddButton} className="w-full" size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Botão
                </Button>
              </div>
            </div>

            {content.buttons && content.buttons.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Botões Adicionados</label>
                {content.buttons.map(button => (
                  <div
                    key={button.id}
                    className="flex items-center justify-between p-2.5 bg-muted/50 rounded-lg border"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{button.text}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {button.type === 'url' && `Link · ${button.value}`}
                        {button.type === 'phone' && `Telefone · ${button.value}`}
                        {button.type === 'text' && 'Resposta de texto'}
                        {button.type === 'postback' && `Ação · ${button.value}`}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => handleRemoveButton(button.id)}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

      </Tabs>

      {/* Sempre visível, e não escondida numa terceira aba: a prévia só ajuda
          enquanto a pessoa escreve. */}
      <div className="@2xl:sticky @2xl:top-0">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
          Como o contato vê
        </p>
        <PreviaDoWhatsApp content={content} />
      </div>
    </div>
  );
};